import { useMemo } from "react";
import { useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { AirCarrierDTO } from "../response.types";
import { PartialBy } from "shared/lib/typeCheck.lib";

import { airCarriersService } from "../airCarriers.api";

type ResetAirCarriersQueryFunc = () => Promise<void>;

export type AirCarrierOption = {
  name: string;
  iataCode: string;
  awbPrefix: string;
};

export type UseAirCarriersQueryReturn = {
  getAirCarriers: () => void;
  resetAirCarriersQuery: ResetAirCarriersQueryFunc;
  airCarriers: AirCarrierDTO[];
  airCarrierOptions: AirCarrierOption[];
  isAirCarriersSuccess: boolean;
  isAirCarriersError: boolean;
  isAirCarriersLoading: boolean;
};

type UseAirCarriersQueryParams = Pick<PartialBy<UseQueryOptions, "queryKey">, "queryKey"> & {
  requestOnMount?: boolean;
};

const AIR_CARRIERS_CACHE_KEY = "get_air_carriers_query";

export const useAirCarriersQuery = ({
  queryKey = [AIR_CARRIERS_CACHE_KEY],
  requestOnMount,
}: UseAirCarriersQueryParams = {}): UseAirCarriersQueryReturn => {
  const queryClient = useQueryClient();

  const { data, isError, isLoading, isSuccess } = useQuery({
    queryKey,
    queryFn: airCarriersService.getAirCarriers,
    select: (response) => response?.carriers ?? [],
    enabled: Boolean(requestOnMount),
  });

  const resetAirCarriersQuery: ResetAirCarriersQueryFunc = () => {
    return queryClient.resetQueries({ queryKey });
  };

  const getAirCarriers = () => {
    queryClient.invalidateQueries({ queryKey });
  };

  const airCarrierOptions = useMemo<AirCarrierOption[]>(() => {
    if (!Array.isArray(data)) return [];

    return data.reduce<AirCarrierOption[]>((acc, carrier) => {
      if (carrier.status?.is_active && carrier.identifiers?.iata_code) {
        acc.push({
          name: carrier.name,
          iataCode: carrier.identifiers.iata_code,
          awbPrefix: carrier.identifiers.awb_prefix,
        });
      }
      return acc;
    }, []);
  }, [data]);

  return {
    resetAirCarriersQuery,
    getAirCarriers,
    airCarriers: data ?? [],
    airCarrierOptions,
    isAirCarriersSuccess: isSuccess,
    isAirCarriersError: isError,
    isAirCarriersLoading: isLoading,
  };
};
